<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { shiftsApi, type Shift } from '@/api/shifts';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import ShiftShow from './actions/shift-show.vue';

const emit = defineEmits(['success', 'refetch']);

type InitialTab = 'general' | 'amounts' | 'report' | 'summary' | 'comments' | 'history';
type InitialReportTab = 'reportImages' | 'reportTexts' | 'vitalSigns' | 'taskRecords' | 'incidentRecords' |
  'medications';

const isOpen = ref(false);
const selectedShiftId = ref<number | null>(null);
const mounted = ref(false);
const initialTab = ref<InitialTab>('general');
const initialReportTab = ref<InitialReportTab>('reportImages');

const queryClient = useQueryClient();

function onRefetch() {
  queryClient.invalidateQueries({ queryKey: ['shift', selectedShiftId.value] });
  emit('refetch');
}

const { data: shiftData } = useQuery({
  queryKey: ['shift', selectedShiftId],
  queryFn: () => shiftsApi.show(selectedShiftId.value!),
  enabled: computed(() => isOpen.value && !!selectedShiftId.value),
});

function open(
  shiftId: number,
  options?: { initialTab?: InitialTab, initialReportTab?: InitialReportTab },
) {
  mounted.value = false;
  initialTab.value = options?.initialTab ?? 'general';
  initialReportTab.value = options?.initialReportTab ?? 'reportImages';
  selectedShiftId.value = shiftId;
  isOpen.value = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      mounted.value = true;
    });
  });
}

defineExpose({ open, initialTab, initialReportTab });
</script>
<template>
  <responsive-dialog
    v-model="isOpen"
    max-width="1000"
    scrollable
  >
    <v-card
      v-if="!mounted || !shiftData"
      min-height="300"
    >
      <v-card-text
        class="d-flex flex-column justify-center align-center"
        style="height: 300px;"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="48"
        />
        <p class="mt-4 text-body-2 text-medium-emphasis">
          Cargando información del turno...
        </p>
      </v-card-text>
    </v-card>
    <shift-show
      v-else
      :key="selectedShiftId!"
      :shift="(shiftData as Shift)"
      :initial-tab="initialTab"
      :initial-report-tab="initialReportTab"
      @close="isOpen = false"
      @success="emit('success')"
      @refetch="onRefetch"
    />
  </responsive-dialog>
</template>
